import React from 'react';
import { Link } from 'react-router-dom';
import { Card, StatusBadge, EmptyState } from '@/components/ds';
import { Users, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * AgencyAgentsSummary — replaces the "Agents" tab. Shows a compact preview of
 * up to 5 direct agents with a "See all agents" pivot into the global roster.
 */
export const AgencyAgentsSummary = ({ agents = [], agencyId }) => {
  const preview = agents.slice(0, 5);
  return (
    <Card
      title="Direct Agents"
      description={`${agents.length} agent${agents.length === 1 ? '' : 's'} tied directly to this agency.`}
      headerActions={
        <div className="flex items-center gap-2">
          <Link to={`/agents?add_agency=${agencyId}`}>
            <Button size="sm" data-testid="agency-add-agent-btn">
              <Plus size={14} className="mr-1" /> Add Agent
            </Button>
          </Link>
          {agents.length > 0 && (
            <Link
              to={`/agents?agency=${agencyId}`}
              className="text-xs font-medium hover:underline flex items-center gap-1"
              style={{ color: 'var(--kr-navy-700)' }}
              data-testid="agency-agents-see-all"
            >
              See all <ChevronRight size={13} />
            </Link>
          )}
        </div>
      }
      testId="agency-agents-summary"
    >
      {agents.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No direct agents"
          description="No agents belong to this agency directly. Downline agencies may still carry agents."
        />
      ) : (
        <div className="space-y-1.5">
          {preview.map((a) => (
            <Link
              key={a.npn}
              to={`/agents/${a.npn}`}
              className="flex items-center justify-between p-2 -mx-2 rounded-md hover:bg-slate-50 transition-colors"
              data-testid={`agency-agent-row-${a.npn}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate" style={{ color: 'var(--kr-navy-900)' }}>
                  {a.first_name} {a.last_name}
                </div>
                <div className="text-[11px] font-mono" style={{ color: 'var(--kr-muted)' }}>
                  NPN {a.npn}{a.email ? ` · ${a.email}` : ''}
                </div>
              </div>
              <StatusBadge variant={a.agent_status === 'active' ? 'success' : a.agent_status === 'onboarding' ? 'warning' : 'neutral'}>
                {a.agent_status}
              </StatusBadge>
            </Link>
          ))}
          {agents.length > preview.length && (
            <div className="text-[11px] pt-2 text-right" style={{ color: 'var(--kr-muted)' }}>
              +{agents.length - preview.length} more
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default AgencyAgentsSummary;
